import type {
  CompanySubscriptionDetailsEntry,
  FullCompanyApiType,
} from "../../../../util/types";
import type { SubscriptionFeeSchedule } from "@shared_packages/toolkit/types";
import {
  PaymentsInterimBillingInterval,
  PaymentsPricingMode,
  SubscriptionFeePricingStrategy,
  SubscriptionLevelEnum,
  SubscriptionOptionEnum,
} from "@shared_packages/toolkit/types";
import {
  emptySchedule,
  formatFeeScheduleSummary,
  formatMoneyFromPence,
  isCustomFeesWizardMode,
  normalizeSchedule,
  paymentsPricingModeToWizardMode,
  scheduleFromLegacyScalars,
} from "./subscription-billing.helpers";

export type WizardStep =
  | "type"
  | "tier"
  | "pricing_mode"
  | "configure_uk"
  | "configure_eu"
  | "interim"
  | "trial_discount"
  | "billing_provider"
  | "review";

const TOOLKIT_FLOW: WizardStep[] = ["type", "tier", "trial_discount", "review"];
const PAYMENTS_FLOW: WizardStep[] = [
  "type",
  "pricing_mode",
  "configure_uk",
  "interim",
  "trial_discount",
  "review",
];
const EU_PAYMENTS_FLOW: WizardStep[] = [
  "type",
  "pricing_mode",
  "configure_eu",
  "interim",
  "trial_discount",
  "review",
];

export function getFlow(
  type: SubscriptionOptionEnum,
  lockedType?: SubscriptionOptionEnum,
  options: { includeBillingProvider?: boolean } = {}
): WizardStep[] {
  const base =
    type === SubscriptionOptionEnum.PAYMENTS_ONLY
      ? PAYMENTS_FLOW
      : type === SubscriptionOptionEnum.PAYMENTS_ONLY_EU
      ? EU_PAYMENTS_FLOW
      : TOOLKIT_FLOW;
  const flow = lockedType ? base.filter((step) => step !== "type") : base;
  if (!options.includeBillingProvider) return flow;
  // Last step before review: whether it applies (not Bespoke) is only known
  // once the pricing mode has been chosen.
  return [...flow.slice(0, -1), "billing_provider", "review"];
}

export const STEP_LABELS: Record<WizardStep, string> = {
  type: "Subscription type",
  tier: "Tier & billing",
  pricing_mode: "Pricing mode",
  configure_uk: "UK payments",
  configure_eu: "EU payments",
  interim: "Interim billing",
  trial_discount: "Status & Discount",
  billing_provider: "Billing provider",
  review: "Review",
};

export function resolvePricingMode(
  data: FullCompanyApiType
): PaymentsPricingMode {
  if (data.hasBespokePricing) return PaymentsPricingMode.BESPOKE;
  return PaymentsPricingMode.RULE_BASED;
}

export function resolvePricingModeFromBilling(
  billing?: CompanySubscriptionDetailsEntry | null,
  data?: FullCompanyApiType
): PaymentsPricingMode {
  if (billing) {
    return paymentsPricingModeToWizardMode(
      billing.paymentsPricingMode,
      billing
    );
  }
  if (data) return resolvePricingMode(data);
  return PaymentsPricingMode.RULE_BASED;
}

export function initialFeeSchedule(
  billing?: CompanySubscriptionDetailsEntry | null,
  data?: FullCompanyApiType
) {
  const existing = billing?.feeSchedules;
  if (existing?.length) {
    return normalizeSchedule(existing);
  }
  if (billing) {
    return emptySchedule();
  }
  return scheduleFromLegacyScalars({
    customFlatFeePerTransaction: data?.customFlatFeePerTransaction,
    customPercentageFee: data?.customPercentageFee,
    customPercentageFeeMinimum: data?.customPercentageFeeMinimum,
  });
}

export function resolveCurrentType(
  data: FullCompanyApiType
): SubscriptionOptionEnum {
  const subs = data.subscriptions ?? [];
  const active = subs.find(
    (s) =>
      !["deleted", "canceled", "cancelled", "deleted_in_process"].includes(
        (s.status ?? "").toLowerCase()
      )
  );
  if (active) {
    if (active.subscriptionOption === SubscriptionOptionEnum.TOOLKIT_MOBILE) {
      return SubscriptionOptionEnum.TOOLKIT_MOBILE;
    }
    if (active.subscriptionOption === SubscriptionOptionEnum.TOOLKIT) {
      return SubscriptionOptionEnum.TOOLKIT;
    }
  }
  return SubscriptionOptionEnum.PAYMENTS_ONLY;
}

export function resolveCurrentLevel(
  data: FullCompanyApiType
): SubscriptionLevelEnum {
  const subs = data.subscriptions ?? [];
  const active = subs.find(
    (s) =>
      !["deleted", "canceled", "cancelled", "deleted_in_process"].includes(
        (s.status ?? "").toLowerCase()
      )
  );
  return active?.subscriptionLevel ?? SubscriptionLevelEnum.STARTER;
}

export const supportsInterimBilling = (mode: PaymentsPricingMode) =>
  isCustomFeesWizardMode(mode);

export const isConfigurablePaymentsMode = (mode: PaymentsPricingMode) =>
  isCustomFeesWizardMode(mode) || mode === PaymentsPricingMode.MONTHLY_FEE;

export function supportsInterimBillingForConfig(
  mode: PaymentsPricingMode,
  fees: SubscriptionFeeSchedule[] | null | undefined
): boolean {
  if (mode !== PaymentsPricingMode.RULE_BASED) return false;
  if (!fees?.length) return false;
  return !fees?.some((schedule) =>
    schedule.rules?.some(
      (rule) =>
        rule.pricingStrategy ===
          SubscriptionFeePricingStrategy.STANDARD_TIERED ||
        rule.pricingStrategy ===
          SubscriptionFeePricingStrategy.HIGH_RISK_TIERED ||
        rule.pricingStrategy ===
          SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN
    )
  );
}

// Mirrors the backend's `productDefaultDays` formula in
// `toolkit-admin-backend/src/services/company/company.ts` (setTrialConfig)
// and `toolkit-backend/src/apis/internal-api/subscriptions/billing/
// subscription-purchase.controller.ts`. Keep in sync if defaults change.
export function getDefaultTrialDays(
  subscriptionOption: SubscriptionOptionEnum,
  isHighRisk: boolean
): number {
  if (
    subscriptionOption === SubscriptionOptionEnum.PAYMENTS_ONLY ||
    subscriptionOption === SubscriptionOptionEnum.PAYMENTS_ONLY_EU
  ) {
    return isHighRisk ? 7 : 3;
  }
  return 14;
}

export const PRICING_MODE_OPTIONS: {
  mode: PaymentsPricingMode;
  label: string;
  description: string;
}[] = [
  {
    mode: PaymentsPricingMode.RULE_BASED,
    label: "Rule based",
    description:
      "Configure pricing per enabled payment currency and payment type.",
  },
  {
    mode: PaymentsPricingMode.MONTHLY_FEE,
    label: "Monthly fee",
    description:
      "One fixed monthly subscription charge in the subscription currency. Payment access is configured separately.",
  },
  {
    mode: PaymentsPricingMode.BESPOKE,
    label: "Bespoke Pricing",
    description: "Manual/custom billing. No automated charging.",
  },
];

export const SUBSCRIPTION_WIZARD_PRICING_MODE_OPTIONS = PRICING_MODE_OPTIONS;

export const PRE_REGISTRATION_PRICING_MODE_OPTIONS =
  PRICING_MODE_OPTIONS.filter(
    ({ mode }) => mode !== PaymentsPricingMode.MONTHLY_FEE
  );

export const INTERIM_INTERVAL_OPTIONS: {
  value: PaymentsInterimBillingInterval | null;
  label: string;
  description: string;
}[] = [
  {
    value: null,
    label: "Monthly only",
    description: "Charge once at the end of the billing period.",
  },
  {
    value: PaymentsInterimBillingInterval.WEEKLY,
    label: "Weekly",
    description: "Charge accumulated fees every 7 days.",
  },
  {
    value: PaymentsInterimBillingInterval.BIWEEKLY,
    label: "Bi-weekly",
    description: "Charge accumulated fees every 14 days.",
  },
  {
    value: PaymentsInterimBillingInterval.DAILY,
    label: "Daily",
    description: "Charge accumulated fees every day.",
  },
];

export const TIERS = [
  SubscriptionLevelEnum.BASIC,
  SubscriptionLevelEnum.STARTER,
  SubscriptionLevelEnum.SCALE_UP,
  SubscriptionLevelEnum.PROFESSIONAL,
  SubscriptionLevelEnum.ENTERPRISE,
];

export const TIER_LABELS: Record<string, string> = {
  BASIC: "Basic",
  STARTER: "Starter",
  SCALE_UP: "Scale Up",
  PROFESSIONAL: "Professional",
  ENTERPRISE: "Enterprise",
};

export const PRICING_MODE_LABELS: Record<string, string> = {
  [PaymentsPricingMode.RULE_BASED]: "Rule based",
  [PaymentsPricingMode.MONTHLY_FEE]: "Monthly fee",
  [PaymentsPricingMode.BESPOKE]: "Bespoke",
};

/** Build a snapshot of the current active subscription, or company pre-config when no sub exists. */
export function buildCurrentSnapshot(
  data: FullCompanyApiType,
  activeSub?: CompanySubscriptionDetailsEntry
) {
  const currentType = activeSub?.subscriptionOption ?? resolveCurrentType(data);
  const currentLevel =
    activeSub?.subscriptionLevel ?? resolveCurrentLevel(data);
  const currentMode = resolvePricingModeFromBilling(activeSub, data);
  const isToolkit =
    currentType === SubscriptionOptionEnum.TOOLKIT ||
    currentType === SubscriptionOptionEnum.TOOLKIT_MOBILE;

  const feesSource =
    (activeSub?.feeSchedules?.length ? activeSub.feeSchedules : null) ??
    (activeSub
      ? null
      : scheduleFromLegacyScalars({
          customFlatFeePerTransaction: data.customFlatFeePerTransaction,
          customPercentageFee: data.customPercentageFee,
          customPercentageFeeMinimum: data.customPercentageFeeMinimum,
        }));
  const feesSummary =
    currentMode === PaymentsPricingMode.RULE_BASED
      ? formatFeeScheduleSummary(feesSource)
      : currentMode === PaymentsPricingMode.MONTHLY_FEE &&
        activeSub?.monthlyFeeMinor != null
      ? `${formatMoneyFromPence(
          activeSub.monthlyFeeMinor,
          activeSub.currency ?? "gbp"
        )}/month`
      : null;

  const trialConfigLabel =
    activeSub?.trialPeriodMode === "custom"
      ? `Custom (${activeSub.trialPeriodDays}d)`
      : activeSub?.trialPeriodMode === "none"
      ? "No trial"
      : activeSub?.trialPeriodMode === "default"
      ? `Default (${getDefaultTrialDays(
          currentType,
          !!activeSub.hasHighRiskSubscriptionPricing
        )}-day)`
      : activeSub
      ? "Not configured"
      : data.trialPeriodMode === "custom"
      ? `Custom (${data.trialPeriodDays}d)`
      : data.trialPeriodMode === "none"
      ? "No trial"
      : `Default (${getDefaultTrialDays(
          currentType,
          !!data.hasHighRiskSubscriptionPricing
        )}-day)`;
  let trialLabel = trialConfigLabel;
  if (activeSub) {
    const subStatus = String(activeSub.status ?? "").toLowerCase();
    const isTrialingNow =
      subStatus === "trialing" || activeSub.onTrial === true;
    const trialExpiresAt = activeSub.trialExpiresAt
      ? new Date(activeSub.trialExpiresAt)
      : null;
    const hadTrialEnded = Boolean(
      !isTrialingNow && trialExpiresAt && trialExpiresAt.getTime() <= Date.now()
    );
    if (isTrialingNow) {
      trialLabel = `${trialConfigLabel} · current sub: on trial`;
    } else if (hadTrialEnded) {
      trialLabel = `${trialConfigLabel} · current sub: trial ended`;
    }
  }

  return {
    type: currentType.replace(/_/g, " "),
    tier: isToolkit ? TIER_LABELS[currentLevel] ?? currentLevel : null,
    billing: isToolkit ? "Monthly" : null,
    pricingMode: !isToolkit
      ? PRICING_MODE_LABELS[currentMode] ?? currentMode
      : null,
    feesSummary,
    interim: activeSub
      ? (activeSub.subscriptionOption ===
          SubscriptionOptionEnum.PAYMENTS_ONLY ||
          activeSub.subscriptionOption ===
            SubscriptionOptionEnum.PAYMENTS_ONLY_EU) &&
        activeSub.interimBillingInterval
        ? activeSub.interimBillingInterval.charAt(0).toUpperCase() +
          activeSub.interimBillingInterval.slice(1)
        : "None"
      : data.paymentsInterimBillingInterval
      ? data.paymentsInterimBillingInterval.charAt(0).toUpperCase() +
        data.paymentsInterimBillingInterval.slice(1)
      : "None",
    trial: trialLabel,
    discount: activeSub
      ? activeSub.subscriptionDiscount?.percentOff
        ? `${activeSub.subscriptionDiscount.percentOff}% off — ${
            activeSub.subscriptionDiscount.duration === "repeating"
              ? `${
                  activeSub.subscriptionDiscount.durationInMonths ?? "?"
                } months`
              : activeSub.subscriptionDiscount.duration ?? "once"
          }`
        : "None"
      : data.discountPercentOff
      ? `${data.discountPercentOff}% off — ${
          data.discountDuration === "repeating"
            ? `${data.discountDurationInMonths} months`
            : data.discountDuration ?? "once"
        }`
      : "None",
  };
}
